import { OtsDateTime } from '../../components/OtsDateTime';
import { useState, type ReactNode } from 'react';
import type { RrcEp9WasteProfile } from '@ots-frontend/ots-data';
import { OtsMasterDetail } from '../../components/OtsMasterDetail';
import { OtsActionButton } from '../../components/OtsButton';
import { OtsInlineAlert } from '../../components/OtsInlineAlert';
import { OtsDataTableEmpty } from '../../components/OtsDataTableCells';
import { ComplianceStatusBadge } from '../ComplianceStatusBadge';
import { RrcEp9WasteProfileDetails } from './RrcEp9WasteProfileDetails';

export type RrcEp9WasteProfileViewProps = {
  profile: RrcEp9WasteProfile;
  versions?: RrcEp9WasteProfile[];
  loadingVersions?: boolean;
  versionsError?: string;
  onRetry?: () => void;
  onEdit?: (profile: RrcEp9WasteProfile) => void;
  onCopy?: (profile: RrcEp9WasteProfile) => void;
  onDelete?: (profile: RrcEp9WasteProfile) => void;
  canEdit?: (profile: RrcEp9WasteProfile) => boolean;
  canDelete?: (profile: RrcEp9WasteProfile) => boolean;
  readOnly?: boolean;
  compact?: boolean;
  renderPdf?: (profile: RrcEp9WasteProfile) => ReactNode;
  onOpenCompanyProfile?: (id: string) => void;
  onOpenLocationProfile?: (id: string) => void;
};
export const RrcEp9WasteProfileView = ({
  profile,
  versions = [],
  loadingVersions,
  versionsError,
  onRetry,
  onEdit,
  onCopy,
  onDelete,
  canEdit = () => true,
  canDelete = () => true,
  readOnly,
  compact,
  renderPdf,
  onOpenCompanyProfile,
  onOpenLocationProfile,
}: RrcEp9WasteProfileViewProps) => {
  const [selectedId, select] = useState(profile.id);
  const history = [...versions].sort(
    (left, right) => right.version - left.version
  );
  const selected =
    history.find((version) => version.id === selectedId) ?? profile;
  const actions = (
    <div className="flex flex-wrap justify-end gap-2">
      {!readOnly && onCopy && (
        <OtsActionButton size="compact" onClick={() => onCopy(selected)}>
          Copy as new version
        </OtsActionButton>
      )}
      {!readOnly && onEdit && canEdit(selected) && (
        <OtsActionButton
          size="compact"
          variant="primary"
          onClick={() => onEdit(selected)}
        >
          Edit profile
        </OtsActionButton>
      )}
      {!readOnly && onDelete && canDelete(selected) && (
        <OtsActionButton
          size="compact"
          variant="destructive"
          onClick={() => onDelete(selected)}
        >
          Delete profile
        </OtsActionButton>
      )}
    </div>
  );
  return (
    <div className="@container space-y-4">
      {readOnly && (
        <OtsInlineAlert
          title="View only"
          size={compact ? 'compact' : 'default'}
        >
          Open EP-9 Waste Profiles in the owning tenant to manage this record.
        </OtsInlineAlert>
      )}
      <OtsMasterDetail
        navigationTitle="Version history"
        navigationLabel="EP-9 waste profile versions"
        items={history.map((version) => ({
          id: version.id,
          label: `Version ${version.version} · ${version.status ?? 'Unknown'}`,
          ariaLabel: `EP-9 waste profile version ${version.version}`,
          content: (
            <>
              <p className="mb-1 text-sm font-semibold text-[var(--ots-secondary)]">
                Version {version.version}
              </p>
              <ComplianceStatusBadge status={version.status} compact />
              <p className="mt-2 text-xs text-[var(--ots-text-muted)]">
                <OtsDateTime
                  value={version.effectiveStartDttm}
                  empty="Not set"
                />
              </p>
              <p className="mt-1 text-xs text-[var(--ots-text-muted)]">
                <OtsDateTime
                  value={version.effectiveEndDttm}
                  empty="No end date"
                />
              </p>
            </>
          ),
        }))}
        selectedId={selected.id}
        onSelect={select}
        compact={compact}
        navigationState={
          loadingVersions ? (
            <OtsDataTableEmpty
              loading
              loadingText="Loading EP-9 waste profile versions…"
            >
              {null}
            </OtsDataTableEmpty>
          ) : versionsError ? (
            <OtsInlineAlert
              tone="danger"
              title="History unavailable"
              action={
                onRetry && (
                  <OtsActionButton size="compact" onClick={onRetry}>
                    Try again
                  </OtsActionButton>
                )
              }
            >
              {versionsError}
            </OtsInlineAlert>
          ) : !history.length ? (
            <p className="text-sm text-[var(--ots-text-muted)]">
              No additional versions found.
            </p>
          ) : undefined
        }
      >
        <div className="min-w-0 space-y-4">
          <RrcEp9WasteProfileDetails
            profile={selected}
            actions={actions}
            compact={compact}
            onOpenCompanyProfile={onOpenCompanyProfile}
            onOpenLocationProfile={onOpenLocationProfile}
          />
          {renderPdf?.(selected)}
        </div>
      </OtsMasterDetail>
    </div>
  );
};
